import { useState, useCallback } from "react";

/**
 * Neural Audio System
 * Synthesizes short, tech-inspired UI sounds using the Web Audio API.
 * Avoids the need for external binary assets.
 */

export type AudioType =
  | "click"
  | "beep"
  | "hover"
  | "transition"
  | "toggle"
  | "calibrate"
  | "system"
  | "action"
  | "exit";

export interface PlayOptions {
  variant?: "on" | "off";
}

class NeuralAudio {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private isMuted = false;
  private lastPlayed: Map<string, number> = new Map();
  private throttleMs = 100;

  constructor() {
    if (typeof window !== "undefined") {
      this.isMuted = localStorage.getItem("neural_audio_muted") === "true";
    }
  }

  private initCtx() {
    if (typeof window === "undefined") return;

    if (!this.ctx) {
      try {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioContextClass) {
          this.ctx = new AudioContextClass();
          this.masterGain = this.ctx.createGain();
          this.masterGain.connect(this.ctx.destination);
        }
      } catch (err) {
        // Silent fail for AudioContext creation
      }
    }

    if (this.ctx && this.ctx.state === "suspended") {
      this.ctx.resume();
    }
  }

  /**
   * Play a synthesized sound effect
   */
  play(type: AudioType = "click", options?: PlayOptions) {
    if (typeof window === "undefined") return;

    // Fix: Only allow the toggle sound if we are currently muted and the intent is to turn audio ON
    // (variant "on" is used by SoundToggle when unmuting)
    if (this.isMuted) {
      if (type !== "toggle" || options?.variant !== "on") return;
    }

    const throttleTime = type === "transition" ? 500 : this.throttleMs;
    const nowTimestamp = Date.now();
    if (nowTimestamp - (this.lastPlayed.get(type) || 0) < throttleTime) return;
    this.lastPlayed.set(type, nowTimestamp);

    this.initCtx();

    if (!this.ctx || !this.masterGain || this.ctx.state === "closed") return;

    const now = this.ctx.currentTime;

    switch (type) {
      case "transition": {
        [440, 444].forEach((freq) => {
          const osc = this.ctx!.createOscillator();
          const gain = this.ctx!.createGain();
          osc.type = "sine";
          osc.frequency.setValueAtTime(freq, now);
          gain.gain.setValueAtTime(0.12, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
          osc.connect(gain);
          gain.connect(this.masterGain!);
          osc.start(now);
          osc.stop(now + 0.3);
        });
        break;
      }

      case "toggle": {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = "sine";
        const isOn = options?.variant !== "off";
        const startFreq = isOn ? 200 : 400;
        const endFreq = isOn ? 400 : 200;

        osc.frequency.setValueAtTime(startFreq, now);
        osc.frequency.exponentialRampToValueAtTime(endFreq, now + 0.1);
        gain.gain.setValueAtTime(0.15, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);

        osc.connect(gain);
        gain.connect(this.masterGain!);
        osc.start(now);
        osc.stop(now + 0.1);
        break;
      }

      case "calibrate": {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(2500, now);
        osc.frequency.exponentialRampToValueAtTime(1200, now + 0.05);

        gain.gain.setValueAtTime(0.08, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);

        osc.connect(gain);
        gain.connect(this.masterGain!);
        osc.start(now);
        osc.stop(now + 0.05);
        break;
      }

      case "system": {
        const bufferSize = this.ctx.sampleRate * 0.2;
        const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;

        const noise = this.ctx.createBufferSource();
        noise.buffer = buffer;

        const filter = this.ctx.createBiquadFilter();
        filter.type = "highpass";
        filter.frequency.setValueAtTime(3000, now);
        filter.frequency.exponentialRampToValueAtTime(800, now + 0.2);

        const gain = this.ctx.createGain();
        gain.gain.setValueAtTime(0.08, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);

        noise.connect(filter);
        filter.connect(gain);
        gain.connect(this.masterGain!);
        noise.start(now);
        noise.stop(now + 0.2);
        break;
      }

      case "action": {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(150, now);
        osc.frequency.exponentialRampToValueAtTime(60, now + 0.15);

        gain.gain.setValueAtTime(0.18, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);

        osc.connect(gain);
        gain.connect(this.masterGain!);
        osc.start(now);
        osc.stop(now + 0.15);
        break;
      }

      case "exit": {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(1500, now);
        osc.frequency.exponentialRampToValueAtTime(2000, now + 0.2);

        gain.gain.setValueAtTime(0.12, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);

        osc.connect(gain);
        gain.connect(this.masterGain!);
        osc.start(now);
        osc.stop(now + 0.2);
        break;
      }

      case "click": {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(150, now);
        osc.frequency.exponentialRampToValueAtTime(40, now + 0.05);

        gain.gain.setValueAtTime(0.15, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);

        osc.connect(gain);
        gain.connect(this.masterGain!);
        osc.start(now);
        osc.stop(now + 0.05);
        break;
      }

      case "beep": {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(440, now);
        osc.frequency.exponentialRampToValueAtTime(880, now + 0.08);

        gain.gain.setValueAtTime(0.1, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);

        osc.connect(gain);
        gain.connect(this.masterGain!);
        osc.start(now);
        osc.stop(now + 0.08);
        break;
      }

      case "hover": {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(800, now);

        gain.gain.setValueAtTime(0.02, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.02);

        osc.connect(gain);
        gain.connect(this.masterGain!);
        osc.start(now);
        osc.stop(now + 0.02);
        break;
      }
    }
  }

  toggleMute() {
    this.isMuted = !this.isMuted;
    localStorage.setItem("neural_audio_muted", String(this.isMuted));
    return this.isMuted;
  }

  getMuted() {
    return this.isMuted;
  }
}

let neuralAudioInstance: NeuralAudio | null = null;
const getAudioInstance = () => {
  if (typeof window === "undefined") return null;
  if (!neuralAudioInstance) {
    neuralAudioInstance = new NeuralAudio();
  }
  return neuralAudioInstance;
};

/**
 * Hook to interface with the Neural Audio system
 */
export const useNeuralAudio = () => {
  const [isMuted, setIsMuted] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("neural_audio_muted") === "true";
  });

  const toggleMute = useCallback(() => {
    const instance = getAudioInstance();
    if (instance) {
      const newMuted = instance.toggleMute();
      setIsMuted(newMuted);
    }
  }, []);

  const play = useCallback((type: AudioType = "click", options?: PlayOptions) => {
    const instance = getAudioInstance();
    if (instance) {
      instance.play(type, options);
    }
  }, []);

  return { isMuted, toggleMute, play };
};
